import { z } from 'zod';
import { ACCEPTED_VIDEO_MIME_TYPES, MAX_VIDEO_SIZE_BYTES, UPLOAD_PART_SIZE_BYTES, } from '../constants.js';
import { videoStatusSchema } from '../enums.js';
import { cursorPaginationSchema } from './pagination.js';
/* ------------------------------------------------------------------ *
 * Upload — flux multipart direct navigateur → stockage objet (FR-8)
 * ------------------------------------------------------------------ */
export const initiateUploadSchema = z.object({
    fieldSessionId: z.string().cuid2(),
    filename: z.string().trim().min(1).max(255),
    sizeBytes: z
        .number()
        .int()
        .positive()
        .max(MAX_VIDEO_SIZE_BYTES, 'Fichier trop volumineux (20 Go maximum)'),
    contentType: z.enum(ACCEPTED_VIDEO_MIME_TYPES),
    title: z.string().trim().max(200).optional(),
});
/**
 * Réponse d'initiation : l'API a créé la vidéo, ouvert l'upload multipart côté stockage et
 * signé une URL par part. Le navigateur téléverse ensuite sans repasser par l'API.
 */
export const initiateUploadResponseSchema = z.object({
    videoId: z.string(),
    uploadId: z.string(),
    storageKey: z.string(),
    partSizeBytes: z.literal(UPLOAD_PART_SIZE_BYTES),
    parts: z.array(z.object({
        partNumber: z.number().int().positive(),
        url: z.string().url(),
    })),
    expiresAt: z.coerce.date(),
});
/** Re-signature des parts restantes après expiration ou rechargement de page (FR-7). */
export const resumeUploadSchema = z.object({
    videoId: z.string().cuid2(),
    /** Numéros de parts déjà confirmées côté stockage, à ne pas re-signer. */
    completedPartNumbers: z.array(z.number().int().positive()).default([]),
});
export const completeUploadSchema = z.object({
    videoId: z.string().cuid2(),
    parts: z
        .array(z.object({
        partNumber: z.number().int().positive(),
        /** ETag renvoyé par le stockage pour la part — requis pour la finalisation S3. */
        etag: z.string().min(1),
    }))
        .min(1),
});
export const abortUploadSchema = z.object({
    videoId: z.string().cuid2(),
});
/* ------------------------------------------------------------------ *
 * Vidéo
 * ------------------------------------------------------------------ */
/** Caractéristiques techniques extraites par ffprobe à l'ingestion (FR-12). */
export const videoTechnicalMetadataSchema = z.object({
    durationSeconds: z.number().nonnegative(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    frameRate: z.number().positive(),
    videoCodec: z.string(),
    audioCodec: z.string().nullable(),
    bitrateKbps: z.number().int().nonnegative().nullable(),
    /** Date de captation lue dans les métadonnées du conteneur, souvent absente. */
    recordedAt: z.coerce.date().nullable(),
});
export const updateVideoSchema = z.object({
    title: z.string().trim().min(1).max(200).optional(),
    description: z.string().trim().max(5000).optional(),
    tags: z.array(z.string().trim().min(1).max(40)).max(50).optional(),
    /** Paires clé/valeur libres définies par l'organisation (FR-18). */
    customFields: z.record(z.string().max(60), z.string().max(500)).optional(),
    /** Rattache la vidéo à une autre session de vol. */
    fieldSessionId: z.string().cuid2().optional(),
});
export const videoSchema = z.object({
    id: z.string(),
    organizationId: z.string(),
    fieldSessionId: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    status: videoStatusSchema,
    /** Motif d'échec lisible, renseigné uniquement en statut FAILED (FR-15). */
    failureReason: z.string().nullable(),
    filename: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    tags: z.array(z.string()),
    customFields: z.record(z.string(), z.string()),
    technical: videoTechnicalMetadataSchema.nullable(),
    hasTelemetry: z.boolean(),
    annotationCount: z.number().int().nonnegative(),
    uploadedById: z.string().nullable(),
    /** Nom affiché de l'auteur du téléversement, joint pour l'affichage (FR-26). */
    uploadedByName: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/** Vue allégée pour les listes : ce qu'il faut afficher sans charger tout le détail. */
export const videoListItemSchema = z.object({
    id: z.string(),
    fieldSessionId: z.string(),
    title: z.string(),
    status: videoStatusSchema,
    filename: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    durationSeconds: z.number().nonnegative().nullable(),
    hasCover: z.boolean(),
    /**
     * URL pré-signée de la couverture (`cover.jpg`), ou `null` si la vidéo n'en a pas encore. Fournie
     * directement dans la liste pour que les vignettes s'affichent sans une requête par carte.
     */
    coverUrl: z.string().url().nullable(),
    /** Nom de l'appareil de captation rattaché à la session de vol (ADR-0039), ou `null`. */
    assetName: z.string().nullable(),
    /** Nom affiché de l'auteur du téléversement (synthèse sur la carte). */
    uploadedByName: z.string().nullable(),
    /** Catégories d'objets annotés sur la vidéo et leur nombre (synthèse sur la carte). */
    labelSummary: z.array(z.object({
        name: z.string(),
        color: z.string(),
        count: z.number().int().positive(),
    })),
    createdAt: z.coerce.date(),
});
/** URLs signées de lecture, renouvelées en cours de session (NFR-5). */
export const playbackResponseSchema = z.object({
    /** Playlist maître HLS. */
    hlsUrl: z.string().url(),
    coverUrl: z.string().url().nullable(),
    spriteUrl: z.string().url().nullable(),
    spriteVttUrl: z.string().url().nullable(),
    expiresAt: z.coerce.date(),
});
/**
 * URL signée de lecture de la **source** (le fichier téléversé, non transcodé).
 *
 * Sert l'annotation avant même que le transcodage HLS n'ait produit ses variantes : on lit
 * l'octet d'origine via une URL pré-signée, ce qui suffit pour poser des boîtes englobantes sur
 * les frames. Les coordonnées étant normalisées dans `[0,1]`, elles resteront valables sur les
 * rendus HLS ultérieurs. Émise à la demande, à durée de vie courte, jamais persistée (NFR-5).
 */
export const sourcePlaybackResponseSchema = z.object({
    url: z.string().url(),
    contentType: z.string(),
    expiresAt: z.coerce.date(),
});
/* ------------------------------------------------------------------ *
 * Recherche (FR-19 à FR-22)
 * ------------------------------------------------------------------ */
/** Emprise géographique : [ouest, sud, est, nord] en degrés décimaux, WGS 84. */
export const boundingBoxSchema = z
    .tuple([
    z.number().min(-180).max(180),
    z.number().min(-90).max(90),
    z.number().min(-180).max(180),
    z.number().min(-90).max(90),
])
    .refine(([w, s, e, n]) => w < e && s < n, {
    message: 'Emprise invalide : attendu [ouest, sud, est, nord]',
});
export const searchVideosSchema = cursorPaginationSchema.extend({
    /** Requête plein texte, interprétée par `websearch_to_tsquery`. */
    q: z.string().trim().max(200).optional(),
    campaignId: z.string().cuid2().optional(),
    fieldSessionId: z.string().cuid2().optional(),
    status: videoStatusSchema.optional(),
    tags: z.array(z.string()).max(20).optional(),
    labelIds: z.array(z.string().cuid2()).max(20).optional(),
    pilotName: z.string().trim().max(100).optional(),
    aircraftModel: z.string().trim().max(100).optional(),
    /** Appareil de captation rattaché à la session (ADR-0039) — filtre « appareil » structuré (FR-20). */
    assetId: z.string().cuid2().optional(),
    flownFrom: z.coerce.date().optional(),
    flownTo: z.coerce.date().optional(),
    hasAnnotations: z.boolean().optional(),
    /** Restreint aux vidéos dont la trace de vol traverse cette emprise (FR-21). */
    bbox: boundingBoxSchema.optional(),
    sortBy: z.enum(['createdAt', 'flownAt', 'title', 'duration']).default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
/**
 * Emprise d'une trace de vol, dénormalisée (sans PostGIS). Sert de repère au sélecteur d'emprise
 * géographique « souverain » (canevas sans tuiles) : on y situe les vols de l'organisation pour que
 * l'utilisateur dessine sa zone de recherche en contexte, sans fond cartographique externe.
 */
export const videoTrackExtentSchema = z.object({
    videoId: z.string(),
    minLat: z.number(),
    minLon: z.number(),
    maxLat: z.number(),
    maxLon: z.number(),
});
/** Page de résultats de recherche : les vidéos (vue allégée) et le curseur de page suivante. */
export const searchVideosResponseSchema = z.object({
    items: z.array(videoListItemSchema),
    /** Curseur à repasser pour la page suivante (`cursor`), `null` s'il n'y en a plus. */
    nextCursor: z.string().nullable(),
});
//# sourceMappingURL=video.js.map